import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Layers, Phone, TriangleAlert } from 'lucide-react';
import { approveCallTask, sendDecision, type CallItem, type DecisionItem } from '@/api/calls';
import { Header } from '@/components/Header';
import { EmptyState } from '@/components/EmptyState';
import { CallRow } from '@/components/calls/CallRow';
import { CallSheet } from '@/components/calls/CallSheet';
import { CallLayer, seedOf } from '@/components/calls/CallLayer';
import { DecisionCard } from '@/components/calls/DecisionCard';
import { MeetingSheet } from '@/components/calendar/NewItemSheets';
import { useProject } from '@/contexts/ProjectProvider';
import { useCallFlow } from '@/hooks/useCallFlow';
import { callsKeys, rememberedScope, useCallsJournal, useDecisions, useMissedOpen } from '@/hooks/useCallsData';
import { useOpenDeal } from '@/hooks/useOpenDeal';
import {
  decisionRequest, decisionsReducer, initialDecisions, visibleDecisions, type DecisionAction,
} from '@/lib/callDecisions';
import {
  CALL_KINDS, CALL_PERIODS, callDayLabel, callName, groupByDay, matchesKind, mineOnPhone, periodLabel, talkMinutes, uniqueCalls,
} from '@/lib/callsQuery';
import { useWorkUi } from '@/store/workUi';
import { useToast } from '@/store/toast';

const plural = (n: number, a: string, b: string, c: string) => {
  const m = n % 10, h = n % 100;
  return m === 1 && h !== 11 ? a : m >= 2 && m <= 4 && (h < 12 || h > 14) ? b : c;
};

/**
 * «Звонки» — как раздел «Звонки» веб-CRM (CallsPage.tsx): журнал по дням, виды, период, сводка,
 * сверху «Ждут вашего решения» (итоги звонков), окно звонка с записью и расшифровкой.
 * Живые обновления — лента CRM (call.ringing / call.finished / call.changed / call.decision), см. useCallsLive в оболочке.
 */
export default function CallsPage() {
  const { status, projectId } = useProject();
  const view = useWorkUi((s) => s.calls);
  const setView = useWorkUi((s) => s.setCalls);
  const qc = useQueryClient();
  const toast = useToast((t) => t.show);
  const openDeal = useOpenDeal();
  const [menu, setMenu] = useState(false);
  const [opened, setOpened] = useState<CallItem | null>(null);
  const [meetingFor, setMeetingFor] = useState<DecisionItem | null>(null);
  const [seed, setSeed] = useState(0);

  const scope0 = rememberedScope(projectId);
  const { list, summary, req, enabled } = useCallsJournal(view, scope0);
  const scope = list.data?.pages[0]?.scope ?? scope0;
  const teamAllowed = scope !== null && scope !== 'own';
  // Область пришла «own» — «все звонки команды» недоступны: возвращаем «мои».
  useEffect(() => { if (scope === 'own' && !view.mine) setView({ mine: true }); }, [scope, view.mine, setView]);

  const decisions = useDecisions();
  const missed = useMissedOpen();
  const [dec, dispatch] = useReducer(decisionsReducer, initialDecisions);
  const decIds = useMemo(() => (decisions.data?.items ?? []).map((i) => i.callId).join(','), [decisions.data]);
  useEffect(() => { dispatch({ type: 'synced', ids: decIds ? decIds.split(',') : [] }); }, [decIds]);
  const decItems = visibleDecisions(decisions.data?.items ?? [], dec);

  const call = useCallFlow({ name: '' });
  const callTo = useCallback((c: CallItem) => {
    setSeed(seedOf(c.dealId ?? c.phone ?? c.id));
    void call.start(c.phone, { dealId: c.dealId ?? undefined, personId: c.personId ?? undefined, name: callName(c) });
  }, [call]);

  const all = useMemo(() => uniqueCalls((list.data?.pages ?? []).map((p) => p.items)), [list.data]);
  // Отбор вида на телефоне — только когда сервер старый и «мои» пришли прежним путём (tab=mine).
  const local = list.data?.pages[0]?.legacyMine ? (req.legacy?.local ?? null) : null;
  const shown = useMemo(() => (local ? all.filter((c) => matchesKind(c, local)) : all), [all, local]);
  const groups = useMemo(() => groupByDay(shown), [shown]);

  // Режим «мои + вид на телефоне»: отобранных мало, а страницы ещё есть — догружаем сами (не больше 8 страниц).
  useEffect(() => {
    if (!local || !list.hasNextPage || list.isFetchingNextPage) return;
    if (shown.length < 20 && (list.data?.pages.length ?? 0) < 8) void list.fetchNextPage();
  }, [local, shown.length, list.hasNextPage, list.isFetchingNextPage, list.data?.pages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Догрузка при прокрутке: низ списка показался.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !list.hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting) && !list.isFetchingNextPage) void list.fetchNextPage();
    }, { rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = useCallback(async (item: DecisionItem, a: DecisionAction) => {
    dispatch({ type: 'start', callId: item.callId, hide: true });
    try {
      const r = decisionRequest(item.callId, a);
      const res = await sendDecision(r.path, r.body);
      // «Принять» в вебе ставит и поручения из разговора, у которых уже подобран исполнитель.
      if (a.kind === 'accept') {
        for (const p of item.tasks?.proposals ?? []) {
          if (!p.decision && p.assignee) await approveCallTask(item.callId, p.id).catch(() => undefined);
        }
      }
      dispatch({ type: 'done', callId: item.callId });
      toast(res.taskProblem ? `Решение сохранено. Не все задачи встали: ${res.taskProblem}` : 'Решение сохранено', 2600);
      void qc.invalidateQueries({ queryKey: callsKeys.decisions(projectId) });
      void qc.invalidateQueries({ queryKey: ['crm', 'cal', projectId] });
    } catch (e) {
      dispatch({ type: 'failed', callId: item.callId, message: e instanceof Error ? e.message : 'Не удалось сохранить решение' });
    }
  }, [qc, projectId, toast]);

  const title = view.mine || !teamAllowed ? 'Мои звонки' : 'Все звонки';
  const menuEl = menu && (
    <>
      <div className="scrim" onClick={() => setMenu(false)} aria-hidden />
      <div className="menu" role="menu">
        <div className="mh">Чьи звонки</div>
        <button type="button" role="menuitemradio" aria-checked={view.mine || !teamAllowed} onClick={() => { setView({ mine: true }); setMenu(false); }}>
          <Check aria-hidden />Мои
        </button>
        {teamAllowed ? (
          <button type="button" role="menuitemradio" aria-checked={!view.mine} onClick={() => { setView({ mine: false }); setMenu(false); }}>
            <Check aria-hidden />Все звонки команды
          </button>
        ) : (
          <div className="lock">{scope === null ? 'Проверяем доступ…' : 'Звонки команды видит руководитель'}</div>
        )}
        <hr />
        <div className="mh">Период</div>
        {CALL_PERIODS.map((p) => (
          <button key={p.key} type="button" role="menuitemradio" aria-checked={view.period === p.key} onClick={() => { setView({ period: p.key }); setMenu(false); }}>
            <Check aria-hidden />{p.label}
          </button>
        ))}
      </div>
    </>
  );

  if (status !== 'ready') return <><Header title="Звонки" subtitle="Загружаем проект…" /><div className="scr" /></>;
  if (!projectId) {
    return <><Header title="Звонки" /><div className="scr"><EmptyState icon={Layers} title="Проект не выбран" text="Выберите проект в разделе «Ещё»." /></div></>;
  }

  // Сводка: сервер считает по всему, что человек видит. В режиме «мои» у руководителя сервер (с 11.10)
  // режет её сам — минуты и «не перезвонили» тоже мои. Старый сервер mine=1 не понял (`sum.mine` нет) —
  // прежний путь: число моих звонков из разреза tabs.mine, минуты не показываем, «не перезвонили» — из моих звонков.
  const phoneMine = mineOnPhone(view, scope);
  const sum = summary.data;
  const legacyMine = phoneMine && !!sum && sum.mine !== true;
  const count = legacyMine ? sum?.tabs.mine : sum?.total;
  const missedOpen = legacyMine ? (missed.data?.count ?? 0) : (sum?.missedNotCalledBack ?? 0);

  return (
    <>
      <Header
        title={title}
        subtitle={periodLabel(view.period)}
        onTitleClick={() => setMenu((m) => !m)}
        titleExpanded={menu}
        menu={menuEl}
      />
      <div className="utabs" role="group" aria-label="Вид звонков">
        {CALL_KINDS.map((k) => (
          <button key={k.key} type="button" className="utab" aria-pressed={view.kind === k.key} onClick={() => setView({ kind: k.key })}>
            {k.label}
            {k.key === 'missed' && missedOpen > 0 && <span className="n tnum" aria-label={`${missedOpen} не перезвонили`}>{missedOpen}</span>}
          </button>
        ))}
      </div>
      <div className="scr">
        {view.kind === 'all' && decItems.length > 0 && (
          <section aria-label="Ждут вашего решения">
            <div className="ah">Ждут вашего решения<span className="n tnum">{decItems.length}</span></div>
            {decItems.map((it) => (
              <DecisionCard
                key={it.callId}
                item={it}
                stages={(it.deal.pipelineId && decisions.data?.stages[it.deal.pipelineId]) || []}
                quickTimes={decisions.data?.quickTimes ?? []}
                project={projectId}
                busy={dec.busy === it.callId}
                error={dec.errors[it.callId] ?? null}
                onAction={(a) => void decide(it, a)}
                onMeeting={() => setMeetingFor(it)}
                onOpenDeal={() => void openDeal(it.deal.id)}
              />
            ))}
          </section>
        )}
        {view.kind === 'all' && decisions.data?.blocked && (
          <p className="calnote" role="status">Звонки через АТС закрыты, пока не решены старые итоги.</p>
        )}

        {sum && (
          <div className="csum tnum">
            {count !== undefined && <span><b>{count}</b> {plural(count, 'звонок', 'звонка', 'звонков')}</span>}
            {!legacyMine && <span><b>{talkMinutes(sum.totalSeconds)}</b> мин разговоров</span>}
            {missedOpen > 0 && <span className="red"><b>{missedOpen}</b> не перезвонили</span>}
          </div>
        )}

        {list.isPending && enabled && (
          <div aria-busy="true" aria-label="Звонки загружаются">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="crow"><span className="sk" style={{ width: 38, height: 38, borderRadius: 12 }} /><span className="cmain"><span className="sk" style={{ height: 14, width: '60%' }} /><span className="sk" style={{ height: 11, width: '40%' }} /></span></div>
            ))}
          </div>
        )}
        {list.isError && (
          list.error && 'status' in list.error && (list.error as { status: number }).status === 403
            ? <EmptyState icon={TriangleAlert} title="Нет доступа к звонкам" text={list.error.message} />
            : <EmptyState icon={TriangleAlert} title="Не удалось прочитать звонки" text={`${list.error.message}. Это не значит, что звонков не было.`} action={{ label: 'Повторить', onClick: () => void list.refetch() }} />
        )}
        {list.isSuccess && shown.length === 0 && !list.hasNextPage && (
          <EmptyState icon={Phone} title="За этот период звонков нет" text="Звонки появляются здесь сами, как только АТС сообщит о разговоре." />
        )}

        {groups.map((g) => (
          <section key={g.key} aria-label={callDayLabel(g.iso)}>
            <div className="dayh">{callDayLabel(g.iso)}</div>
            {g.items.map((c) => (
              <CallRow key={c.id} c={c} showAgent={!view.mine && teamAllowed} onOpen={setOpened} onCallBack={callTo} />
            ))}
          </section>
        ))}
        <div ref={sentinel} className="more-row">
          {list.isFetchingNextPage ? <span className="spinner size-5 inline-block" aria-label="Загружаем ещё" />
            : list.hasNextPage ? <button type="button" onClick={() => void list.fetchNextPage()}>Показать ещё</button>
              : shown.length > 0 ? <span>Показаны все звонки: {shown.length}</span> : null}
        </div>
        <div style={{ height: 24 }} />
      </div>

      {opened && (
        <CallSheet
          initial={opened}
          project={projectId}
          onClose={() => setOpened(null)}
          onCall={(c) => { setOpened(null); callTo(c); }}
          onOpenDeal={(c) => { setOpened(null); void openDeal(c.dealId); }}
        />
      )}
      {meetingFor && (
        <MeetingSheet
          deal={{ id: meetingFor.deal.id, title: meetingFor.deal.title ?? meetingFor.person?.name ?? 'Лид', isLead: true, personName: meetingFor.person?.name ?? null }}
          onClose={() => setMeetingFor(null)}
        />
      )}
      <CallLayer call={call} seed={seed} />
    </>
  );
}
